import { useCallback, useMemo } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  CircleDashed,
  Clock3,
  MinusCircle,
  type LucideIcon,
} from 'lucide-react';
import styled, { css, keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import type { OnboardingTaskStatus } from '../../api/onboarding';
import type { OnboardingTask } from '../../api/onboarding';
import { useMyOnboardingTasks } from '../../hooks/useOnboardingTasks';
import { SmallGrayPanel, backendPalette } from '../backend/BackendCardSystem';

const darkPanelBackground =
  'linear-gradient(180deg, rgba(20,22,28,.96), rgba(18,20,25,.96))';
const darkPanelBorder = 'rgba(255,255,255,.06)';
const darkPanelText = '#E8EAED';
const darkPanelMuted = 'rgba(186, 198, 224, 0.82)';
const darkPanelSoft = 'rgba(186, 198, 224, 0.62)';
const darkTaskSurface = 'rgba(255, 255, 255, 0.03)';
const darkTaskSurfaceHover = 'rgba(255, 255, 255, 0.045)';
const darkAccent = 'rgba(224, 161, 94, 0.88)';

const skeletonPulse = keyframes`
  0% {
    opacity: 0.48;
  }
  50% {
    opacity: 0.92;
  }
  100% {
    opacity: 0.48;
  }
`;

type TaskStatusMeta = {
  label: string;
  icon: LucideIcon;
};

const STATUS_META: Record<OnboardingTaskStatus, TaskStatusMeta> = {
  pending: { label: 'Pending', icon: CircleDashed },
  in_progress: { label: 'In progress', icon: Clock3 },
  completed: { label: 'Completed', icon: CheckCircle2 },
  blocked: { label: 'Blocked', icon: AlertCircle },
  skipped: { label: 'Skipped', icon: MinusCircle },
};

type OnboardingTasksPanelProps = {
  readonly square?: boolean;
  readonly tone?: 'gray' | 'green';
  readonly externalHeader?: boolean;
  readonly fillHeight?: boolean;
  readonly subtleTaskHover?: boolean;
  readonly accentColor?: string;
  readonly className?: string;
};

const Wrap = styled.section<{ $square: boolean; $fillHeight: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  min-height: 0;
  overflow: hidden;
  ${({ $fillHeight }) =>
    $fillHeight
      ? css`
          height: 100%;
        `
      : ''}
  ${({ $square }) =>
    $square
      ? css`
          justify-content: start;
        `
      : ''}
`;

const Card = styled(SmallGrayPanel)<{
  $square: boolean;
  $tone: 'gray' | 'green';
  $fillHeight: boolean;
}>`
  padding: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  flex: 1 1 auto;
  font-weight: 700;
  color: ${({ $tone }) => ($tone === 'green' ? backendPalette.black : darkPanelText)};
  min-height: 0;
  border: 1px solid
    ${({ $tone }) => ($tone === 'green' ? 'rgba(26, 26, 26, 0.28)' : darkPanelBorder)};
  box-shadow: ${({ $tone }) =>
    $tone === 'green' ? 'none' : '0 18px 48px rgba(0,0,0,.28)'};
  overflow: hidden;
  ${({ $fillHeight }) =>
    $fillHeight
      ? css`
          height: 100%;
        `
      : ''}
  ${({ $tone }) =>
    $tone === 'green'
      ? css`
          background: transparent;

          &::after {
            display: none;
          }
        `
      : css`
          background: ${darkPanelBackground};

          &::after {
            opacity: 0.12;
            background:
              linear-gradient(160deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0) 38%),
              linear-gradient(20deg, rgba(0, 0, 0, 0.18), rgba(0, 0, 0, 0) 42%);
          }
        `}
  ${({ $square }) =>
    $square
      ? css`
          width: min(100%, 420px);
          // aspect-ratio: 1 / 1;
          overflow: hidden;
        `
      : ''}
`;

const ExternalHeader = styled.div`
  display: grid;
  gap: 4px;
  padding-left: 1rem;
`;

const ExternalTitle = styled.h3<{ $accentColor?: string }>`
  margin: 0;
  color: ${({ $accentColor, theme }) => $accentColor ?? theme.colors.heading};
  font-size: clamp(1.7rem, 3.5vw, 2.4rem);
  font-weight: 700;
`;

const ExternalSubtext = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
  font-size: 0.95rem;
  font-weight: 700;
`;

const Title = styled.h3<{ $tone: 'gray' | 'green' }>`
  margin: 0;
  color: ${({ $tone }) => ($tone === 'green' ? backendPalette.black : darkPanelText)};
  font-weight: 900;
`;

const Subtext = styled.p<{ $tone: 'gray' | 'green' }>`
  margin: 0;
  color: ${({ $tone }) => ($tone === 'green' ? 'rgba(26, 26, 26, 0.92)' : darkPanelMuted)};
  font-size: 0.92rem;
  font-weight: 700;
`;

const Progress = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.7)};
`;

const ProgressBar = styled.div<{ $pct: number; $tone?: 'gray' | 'green' }>`
  height: 8px;
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === 'green' ? 'rgba(26, 26, 26, 0.34)' : 'rgba(255, 255, 255, 0.08)'};
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $pct }) => `${Math.max(0, Math.min(100, $pct))}%`};
    background: ${({ $tone }) =>
      $tone === 'green'
        ? 'linear-gradient(90deg, #a64c44 0%, #1a1a1a 100%)'
        : 'linear-gradient(90deg, rgba(224, 161, 94, 0.98) 0%, rgba(94, 212, 255, 0.95) 100%)'};
  }
`;

const ExternalProgressBar = styled(ProgressBar)<{ $accentColor?: string }>`
  background: rgba(170, 170, 170, 0.55);

  &::after {
    background: ${({ $accentColor, theme }) => $accentColor ?? theme.colors.heading};
  }
`;

const TaskList = styled.div<{ $square: boolean; $scrollable: boolean }>`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1)};
  min-height: 0;
  max-height: 100%;
  flex: 1 1 auto;
  scrollbar-width: thin;
  scrollbar-color: ${darkAccent} rgba(255, 255, 255, 0.03);

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.03);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${darkAccent};
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.04);
  }

  &::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }

  ${({ $square, $scrollable }) =>
    $square || $scrollable
      ? css`
          overflow: auto;
          padding-right: 4px;
        `
      : ''}
`;

const SkeletonBlock = styled.div<{
  $width?: string;
  $height?: string;
  $radius?: string;
}>`
  width: ${({ $width }) => $width ?? '100%'};
  height: ${({ $height }) => $height ?? '0.95rem'};
  border-radius: ${({ $radius }) => $radius ?? '10px'};
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05),
    rgba(255, 255, 255, 0.12),
    rgba(255, 255, 255, 0.05)
  );
  animation: ${skeletonPulse} 1.4s ease-in-out infinite;
`;

const LoadingStack = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(1)};
  align-content: start;
`;

const LoadingTask = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: ${({ theme }) => theme.spacing(1.1)};
  background: rgba(255, 255, 255, 0.03);
  padding: ${({ theme }) => theme.spacing(1.2)};
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.6)};
`;

const Task = styled.div<{
  $tone: 'gray' | 'green';
  $clickable: boolean;
  $subtleTaskHover: boolean;
}>`
  border: 1px solid
    ${({ $tone }) => ($tone === 'green' ? 'rgba(26, 26, 26, 0.34)' : 'rgba(255, 255, 255, 0.08)')};
  border-radius: ${({ theme }) => theme.spacing(1.1)};
  padding: ${({ theme }) => theme.spacing(1.2)};
  background: ${({ $tone }) =>
    $tone === 'green' ? backendPalette.green : darkTaskSurface};
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.8)};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: border-color 120ms ease, transform 120ms ease, background 120ms ease;

  ${({ $clickable, $subtleTaskHover }) =>
    $clickable
      ? css`
          &:hover {
            border-color: ${$subtleTaskHover ? '#969696' : darkAccent};
            ${$subtleTaskHover
              ? ''
              : css`
                  background: ${darkTaskSurfaceHover};
                  transform: translateY(-1px);
                `}
          }
        `
      : ''}
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  flex-wrap: wrap;
`;

const TaskTitleGroup = styled.div`
  display: grid;
  gap: 2px;
`;

const TaskTitle = styled.div<{ $tone: 'gray' | 'green' }>`
  font-weight: 900;
  color: ${({ $tone }) => ($tone === 'green' ? backendPalette.black : darkPanelText)};
`;

const TaskCategory = styled.div<{ $tone: 'gray' | 'green' }>`
  color: ${({ $tone }) => ($tone === 'green' ? 'rgba(26, 26, 26, 0.74)' : darkPanelSoft)};
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 800;
`;

const TaskDescription = styled.div<{ $tone: 'gray' | 'green' }>`
  color: ${({ $tone }) => ($tone === 'green' ? 'rgba(26, 26, 26, 0.9)' : darkPanelMuted)};
  font-size: 0.9rem;
  font-weight: 700;
`;

const TaskActionHint = styled.div<{ $tone: 'gray' | 'green' }>`
  font-size: 0.78rem;
  font-weight: 800;
  color: ${({ $tone }) => ($tone === 'green' ? 'rgba(26, 26, 26, 0.72)' : darkAccent)};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const StatusPill = styled.div<{ $status: OnboardingTaskStatus; $tone: 'gray' | 'green' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid
    ${({ $tone }) => ($tone === 'green' ? 'rgba(255,255,255,0.18)' : 'rgba(255, 255, 255, 0.09)')};
  background: ${({ $tone }) => ($tone === 'green' ? 'rgba(255,255,255,0.08)' : 'rgba(255, 255, 255, 0.03)')};
  color: ${({ $tone }) => ($tone === 'green' ? 'rgba(255, 255, 255, 0.98)' : darkPanelText)};
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 800;

  svg {
    width: 14px;
    height: 14px;
    stroke-width: 2.1;
  }
`;

function includesAny(haystack: string, needles: readonly string[]): boolean {
  return needles.some((needle) => haystack.includes(needle));
}

function getTaskCategory(task: OnboardingTask): string {
  const source = (task.source ?? '').toLowerCase();
  const title = task.title.toLowerCase();

  if (
    source.includes('onboarding') ||
    includesAny(title, ['profile', 'availability', 'tax', 'setup', 'team', 'discord'])
  ) {
    return 'Onboarding';
  }

  if (source.includes('billing') || includesAny(title, ['billing', 'invoice', 'subscription', 'payment'])) {
    return 'Billing';
  }

  if (source.includes('project') || includesAny(title, ['project', 'milestone', 'brief'])) {
    return 'Project';
  }

  if (source.includes('compliance') || includesAny(title, ['compliance', 'contract', 'legal'])) {
    return 'Compliance';
  }

  return task.assignedByUserId ? 'Assigned' : 'General';
}

function isTwoFactorTask(task: OnboardingTask): boolean {
  const searchBlob = `${task.title} ${task.description ?? ''}`.toLowerCase();
  return includesAny(searchBlob, ['2fa', 'mfa', 'two-factor', 'two factor', 'authenticator']);
}

function isDiscordTask(task: OnboardingTask): boolean {
  const searchBlob = `${task.title} ${task.description ?? ''}`.toLowerCase();
  return includesAny(searchBlob, ['discord']);
}

function isBusinessProfileTask(task: OnboardingTask): boolean {
  const searchBlob = `${task.title} ${task.description ?? ''}`.toLowerCase();
  return includesAny(searchBlob, ['business profile', 'company profile']);
}

export default function OnboardingTasksPanel({
  square = false,
  tone = 'gray',
  externalHeader = false,
  fillHeight = false,
  subtleTaskHover = false,
  accentColor,
  className,
}: OnboardingTasksPanelProps) {
  const navigate = useNavigate();
  const myTasksQuery = useMyOnboardingTasks(true);
  const handleTaskOpen = useCallback(
    (task: OnboardingTask) => {
      if (isTwoFactorTask(task)) {
        navigate('/dashboard/profile');
        return;
      }
      if (isDiscordTask(task)) {
        navigate('/dashboard/profile');
        return;
      }
      if (isBusinessProfileTask(task)) {
        navigate('/dashboard/business');
      }
    },
    [navigate],
  );

  const myTasks = useMemo(
    () => [...(myTasksQuery.data?.tasks ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [myTasksQuery.data?.tasks],
  );
  const progressSummary = myTasksQuery.data
    ? `${myTasksQuery.data.progress.completedTasks} of ${
        myTasksQuery.data.progress.totalTasks
      } complete${
        myTasksQuery.data.progress.nextTask
          ? ` - Next: ${myTasksQuery.data.progress.nextTask}`
          : ''
      }`
    : '';

  return (
    <Wrap $square={square} $fillHeight={fillHeight} className={className}>
      {externalHeader ? (
        <ExternalHeader>
          <ExternalTitle $accentColor={accentColor}>All Tasks</ExternalTitle>
          <ExternalSubtext>
            Your active and completed tasks are listed here.
          </ExternalSubtext>
          {myTasksQuery.data ? <ExternalSubtext>{progressSummary}</ExternalSubtext> : null}
          {myTasksQuery.data ? (
            <ExternalProgressBar
              $pct={myTasksQuery.data.progress.completionPercent}
              $accentColor={accentColor}
            />
          ) : null}
        </ExternalHeader>
      ) : null}
      <Card $square={square} $tone={tone} $fillHeight={fillHeight}>
        {!externalHeader ? (
          <>
            <Title $tone={tone}>All Tasks</Title>
            <Subtext $tone={tone}>
              Your active and completed tasks are listed here.
            </Subtext>
          </>
        ) : null}

        {myTasksQuery.data ? (
          <>
            {!externalHeader ? (
              <Progress>
                <Subtext $tone={tone}>{progressSummary}</Subtext>
                <ProgressBar $pct={myTasksQuery.data.progress.completionPercent} $tone={tone} />
              </Progress>
            ) : null}

            <TaskList $square={square} $scrollable={fillHeight}>
              {myTasks.map((task) => {
                const taskIsTwoFactor = isTwoFactorTask(task);
                const taskIsDiscord = isDiscordTask(task);
                const taskIsBusinessProfile = isBusinessProfileTask(task);
                const taskIsActionable = taskIsTwoFactor || taskIsDiscord || taskIsBusinessProfile;
                return (
                <Task
                  key={task.id}
                  $tone={tone}
                  $clickable={taskIsActionable}
                  $subtleTaskHover={subtleTaskHover}
                  onClick={taskIsActionable ? () => handleTaskOpen(task) : undefined}
                  onKeyDown={
                    taskIsActionable
                      ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleTaskOpen(task);
                          }
                        }
                      : undefined
                  }
                  role={taskIsActionable ? 'button' : undefined}
                  tabIndex={taskIsActionable ? 0 : undefined}
                >
                  <Row>
                    <TaskTitleGroup>
                      <TaskTitle $tone={tone}>{task.title}</TaskTitle>
                      <TaskCategory $tone={tone}>{getTaskCategory(task)}</TaskCategory>
                    </TaskTitleGroup>
                    <StatusPill $status={task.status} $tone={tone}>
                      {(() => {
                        const meta = STATUS_META[task.status];
                        const Icon = meta.icon;
                        return (
                          <>
                            <Icon aria-hidden="true" />
                            <span>{meta.label}</span>
                          </>
                        );
                      })()}
                    </StatusPill>
                  </Row>
                  {task.description ? <TaskDescription $tone={tone}>{task.description}</TaskDescription> : null}
                  {taskIsTwoFactor ? <TaskActionHint $tone={tone}>Open profile settings</TaskActionHint> : null}
                  {taskIsDiscord ? <TaskActionHint $tone={tone}>Connect Discord in profile</TaskActionHint> : null}
                  {taskIsBusinessProfile ? <TaskActionHint $tone={tone}>Open business page</TaskActionHint> : null}
                </Task>
                );
              })}
              {!myTasks.length ? <Subtext $tone={tone}>No tasks assigned yet.</Subtext> : null}
            </TaskList>
          </>
        ) : (
          <LoadingStack>
            <SkeletonBlock $width="52%" $height="0.95rem" />
            <SkeletonBlock $width="100%" $height="8px" $radius="999px" />
            {Array.from({ length: 3 }, (_, index) => (
              <LoadingTask key={`task-loading-${index}`}>
                <SkeletonBlock $width="62%" $height="1rem" />
                <SkeletonBlock $width="88%" $height="0.82rem" />
                <SkeletonBlock $width="54%" $height="0.82rem" />
              </LoadingTask>
            ))}
          </LoadingStack>
        )}
      </Card>
    </Wrap>
  );
}
